/**
 * Format a timestamp (in microseconds) to a relative time string
 * Backend returns timestamps in microseconds (since epoch * 1,000,000)
 * Shows actual date/time for anything older than 24 hours
 * Uses "mins ago" for minutes, "hrs ago" for hours
 */
export function formatRelativeTime(timestampMicroseconds: number): string {
  if (!timestampMicroseconds || isNaN(timestampMicroseconds) || timestampMicroseconds <= 0) {
    return 'Unknown'
  }

  // Convert microseconds to milliseconds for Date constructor
  const date = new Date(timestampMicroseconds / 1000)
  const now = Date.now()

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Unknown'
  }

  const diffMs = now - date.getTime()

  // Handle future dates
  if (diffMs < 0) {
    return 'Just now'
  }

  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)

  // Less than 1 minute
  if (diffSeconds < 60) {
    return 'now'
  }
  // Less than 1 hour
  else if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }
  // Less than 24 hours
  else if (diffHours < 24) {
    return `${diffHours}h`
  }
  // More than 24 hours - show compact date
  else {
    return formatCompactDate(date)
  }
}

/**
 * Format a date object to a compact date string
 * Format: "Jan 15" or "Jan 15, 2024" if different year
 */
function formatCompactDate(date: Date): string {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const now = new Date()
  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()

  // If same year, don't show year
  if (year === now.getFullYear()) {
    return `${month} ${day}`
  }
  return `${month} ${day}, ${year}`
}

/**
 * Format a timestamp for "joined" dates (more friendly for account creation)
 * Backend returns timestamps in microseconds
 * Shows actual date for anything older than a few weeks
 */
export function formatJoinedTime(timestampMicroseconds: number): string {
  if (!timestampMicroseconds || isNaN(timestampMicroseconds) || timestampMicroseconds <= 0) {
    return 'Unknown'
  }

  // Convert microseconds to milliseconds for Date constructor
  const date = new Date(timestampMicroseconds / 1000)
  const now = Date.now()

  if (isNaN(date.getTime())) {
    return 'Unknown'
  }

  const diffMs = now - date.getTime()

  if (diffMs < 0) {
    return 'Today'
  }

  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else if (diffWeeks < 4) {
    return `${diffWeeks} ${diffWeeks === 1 ? 'week' : 'weeks'} ago`
  } else {
    // More than 4 weeks - show actual date
    return formatDate(date)
  }
}

/**
 * Format a date object to a readable date string
 * Format: "Jan 15, 2024"
 */
function formatDate(date: Date): string {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  const month = months[date.getMonth()]
  const day = date.getDate()
  const year = date.getFullYear()

  return `${month} ${day}, ${year}`
}

/**
 * Split pane "Created" cell: first line local time, second line full date.
 * Backend timestamps are epoch **microseconds**.
 */
export function formatMicrosTimeAndDateLines(
  timestampMicroseconds: number
): { time: string; date: string } | null {
  if (!timestampMicroseconds || isNaN(timestampMicroseconds) || timestampMicroseconds <= 0) {
    return null
  }
  const date = new Date(timestampMicroseconds / 1000)
  if (isNaN(date.getTime())) {
    return null
  }
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  const dateLine = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return { time, date: dateLine }
}
